"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const three_1 = require("three");
// eslint-disable-next-line import/no-cycle
const __1 = require("..");
// eslint-disable-next-line import/no-cycle
const utils_1 = require("../../utils");
const consts_1 = __importDefault(require("../../consts/consts"));
const types_1 = require("../../../../store/types");
const PanelInSlotsConsts_1 = require("./PanelInSlotsConsts");
const PanelInSlotsUtils_1 = require("./PanelInSlotsUtils");
class PanelInSlotsHelper {
    constructor(viewer) {
        this.inPanelPlaneObjects = [];
        this.raycaster = new three_1.Raycaster();
        this.extrusionOccurences = [];
        this.notches = [];
        this.tempVector = new three_1.Vector3();
        this.viewer = viewer;
        this.isScaled = false;
        this.insertionErrorMessage = null;
    }
    isPanelScaled() {
        return this.isScaled;
    }
    clearScaledPanel(panel) {
        if (this.isScaled) {
            panel.restoreStandardGeometry();
            this.resetScaleForPanel(panel);
            this.isScaled = false;
        }
    }
    resetScaleForPanel(panel) {
        panel.setScaleX(1);
        panel.setScaleY(1);
        this.insertionErrorMessage = null;
    }
    insert(intersectionPoint, panelToInsert, neighborObject, selectedSlot) {
        this.insertionErrorMessage = null;
        if (!neighborObject ||
            !selectedSlot ||
            (0, PanelInSlotsUtils_1.getExtrusionsAndCornersQuantity)(this.viewer, neighborObject.connectedItems) < 2 ||
            !panelToInsert.visible) {
            return false;
        }
        const panelNormal = (0, PanelInSlotsUtils_1.getPanelNormal)(selectedSlot);
        this.panelPlane = new three_1.Plane().setFromNormalAndCoplanarPoint(panelNormal, selectedSlot.origin);
        this.panel = panelToInsert;
        this.notches = [];
        this.halfOfPanelThickness = panelToInsert.getThickness() / 2;
        const points = this.getPoints(intersectionPoint, neighborObject, selectedSlot);
        if (!points.length) {
            return false;
        }
        const panelStartPoint = (0, PanelInSlotsUtils_1.getContourMaxCorner)((0, PanelInSlotsUtils_1.rotateContourInXY)(points, panelNormal));
        panelToInsert.setRotation(utils_1.Utils.identityQuaternion);
        panelToInsert.setPosition(utils_1.Utils.zeroVector);
        panelToInsert.applyExtrudeGeometry(points, panelNormal.clone());
        panelToInsert.setPosition(panelStartPoint);
        panelToInsert.setRotation(new three_1.Quaternion().setFromUnitVectors(consts_1.default.DP_UP_DIR, panelNormal), utils_1.Utils.zeroVector);
        panelToInsert.translate(panelNormal.clone().negate().multiplyScalar(this.halfOfPanelThickness));
        // eslint-disable-next-line no-param-reassign
        panelToInsert.notchesCount = this.notches.length;
        this.isScaled = true;
        this.validatePanelSize(panelToInsert);
        return true;
    }
    getPoints(intersectionPoint, startExtrusion, selectedSlot) {
        this.extrusionOccurences = [];
        const resultPoints = [];
        let isClosedContour = false;
        const startPosition = (0, PanelInSlotsUtils_1.getStartPosition)(intersectionPoint, selectedSlot);
        this.inPanelPlaneObjects = this.viewer.getScene().children.filter((object) => {
            if (!(object instanceof __1.BaseObject3D)) {
                return false;
            }
            return this.isObjectToHandle(object);
        });
        if (this.inPanelPlaneObjects.length <= 1) {
            return resultPoints;
        }
        let currentObject = startExtrusion;
        const startDirection = (0, PanelInSlotsUtils_1.getStartDirection)(selectedSlot.direction, selectedSlot.normal);
        let currentDirection = startDirection;
        let currentStartPosition = startPosition;
        let currentSlot = selectedSlot;
        let previousObject = null;
        this.extrusionOccurences.push(currentObject.uuid);
        while (currentObject) {
            const nextObjectData = this.getNextObjectData(previousObject, currentObject, currentSlot, currentDirection, currentStartPosition);
            if (!nextObjectData || !nextObjectData.points || !nextObjectData.points.length) {
                break;
            }
            if (nextObjectData.object.uuid === startExtrusion.uuid &&
                utils_1.Utils.areVectorsEqual(nextObjectData.slot.surfacePoint, selectedSlot.surfacePoint) &&
                previousObject !== null) {
                const oppositeFastenersData = this.getOppositeSlotFasteners(startDirection, this.getConnectedObjects(nextObjectData.object, currentObject), nextObjectData.object, nextObjectData.slot, [currentObject.uuid]);
                oppositeFastenersData.forEach((fastenerData) => {
                    if (this.notches.some((notch) => notch.objectId === fastenerData.fastenerId)) {
                        return;
                    }
                    const { fastenerPoints } = fastenerData;
                    const notAddedFastenerPoints = fastenerPoints.filter((currentPoint) => !resultPoints.some((point) => utils_1.Utils.areVectorsEqual(currentPoint, point)));
                    if (notAddedFastenerPoints.length) {
                        nextObjectData.points = nextObjectData.points.concat(notAddedFastenerPoints);
                    }
                });
            }
            nextObjectData.points.forEach((point) => {
                if (resultPoints.length >= 2) {
                    const lastByOnePoint = resultPoints.at(-2);
                    const lastPoint = resultPoints.at(-1);
                    if (utils_1.Utils.isVectorBetweenVectors(lastPoint, lastByOnePoint, point)) {
                        resultPoints.pop();
                    }
                }
                resultPoints.push(point);
            });
            previousObject = currentObject;
            currentObject = nextObjectData.object;
            currentDirection = nextObjectData.contourDirection;
            currentStartPosition = nextObjectData.startPosition;
            currentSlot = nextObjectData.slot;
            this.extrusionOccurences.push(currentObject.uuid);
            // #region prevent infinitive loop
            const count = this.extrusionOccurences.length;
            if (count >= 3) {
                const lastId = this.extrusionOccurences[count - 1];
                if (lastId === this.extrusionOccurences[count - 2] && lastId === this.extrusionOccurences[count - 3]) {
                    break;
                }
            }
            // eslint-disable-next-line no-loop-func
            const currentObjectOccurences = this.extrusionOccurences.filter((id) => id === currentObject.uuid);
            if (currentObjectOccurences.length > 10) {
                break;
            }
            // #endregion
            if (currentObject.uuid === startExtrusion.uuid &&
                utils_1.Utils.areVectorsEqual(currentSlot.surfacePoint, selectedSlot.surfacePoint)) {
                isClosedContour = true;
                break;
            }
        }
        return isClosedContour ? resultPoints : [];
    }
    getConnectedObjects(currentObject, previousObject) {
        const result = currentObject.connectedItems
            .filter((connectionItem) => connectionItem.uuid !== (previousObject === null || previousObject === void 0 ? void 0 : previousObject.uuid))
            .map((connectionItem) => {
            const connectedObject = this.viewer.getObjectById(connectionItem.uuid);
            if (this.inPanelPlaneObjects.some((object) => object.uuid === connectedObject.uuid)) {
                return connectedObject;
            }
            return null;
        })
            .filter((object) => object instanceof __1.ExtrusionObjectsBase ||
            (object instanceof __1.ComponentObjectsBase && object.isCornerComponent));
        return result;
    }
    getNextObjectData(previousObject, currentObject, currentSlot, currentContourDirection, currentObjectStartPosition) {
        const connectedObjects = this.getConnectedObjects(currentObject, previousObject);
        if (!connectedObjects.length) {
            return null;
        }
        const raycastPointPos = currentSlot.normal.clone().setLength(0.1);
        const raycastPoint = currentObjectStartPosition.clone().add(raycastPointPos);
        if (this.isNotPresentAnyOppositeExtrusion(raycastPoint, currentSlot.normal.clone())) {
            return null;
        }
        let nextObjectData = null;
        nextObjectData = this.handleInSlotExtrusions(currentContourDirection, {
            connectedObjects,
            currentObject,
            currentSlot,
            currentObjectStartPosition,
        });
        const hasInSlotExtrusions = nextObjectData !== null;
        nextObjectData !== null && nextObjectData !== void 0 ? nextObjectData : (nextObjectData = this.handleEndConnectedExtrusion(currentContourDirection, {
            connectedObjects,
            currentObject,
            currentSlot,
            currentObjectStartPosition,
        }));
        nextObjectData !== null && nextObjectData !== void 0 ? nextObjectData : (nextObjectData = this.handleEndVerticalExtrusion(currentContourDirection, {
            connectedObjects,
            currentObject,
            currentObjectStartPosition,
            currentSlot,
        }));
        nextObjectData !== null && nextObjectData !== void 0 ? nextObjectData : (nextObjectData = this.handleEndCornerComponents(currentContourDirection, {
            connectedObjects,
            currentObject,
            currentObjectStartPosition,
            currentSlot,
        }));
        nextObjectData !== null && nextObjectData !== void 0 ? nextObjectData : (nextObjectData = this.handleEndOfCurrentExtrusion(currentContourDirection, currentObject, currentSlot));
        if (nextObjectData && currentObject instanceof __1.ExtrusionObjectsBase) {
            this.handleOppositeSlotFasteners(currentContourDirection, {
                connectedObjects,
                currentObject,
                currentSlot,
                currentObjectStartPosition,
            }, previousObject, nextObjectData, hasInSlotExtrusions);
        }
        return nextObjectData;
    }
    handleEndCornerComponents(startDirection, { connectedObjects, currentObject, currentObjectStartPosition, currentSlot }) {
        var _a, _b;
        let cornerComponent = null;
        let connectionPosition = null;
        for (let i = 0; i < connectedObjects.length; i += 1) {
            const connectedObject = connectedObjects[i];
            if (!(connectedObject instanceof __1.ComponentObjectsBase &&
                connectedObject.isCornerComponent &&
                utils_1.Utils.roundTo3(this.tempVector.subVectors(connectedObject.position, currentObjectStartPosition).dot(startDirection)) > 0)) {
                // eslint-disable-next-line no-continue
                continue;
            }
            const currentConnectedItem = connectedObjects[i].connectedItems.some((connectedItem) => connectedItem.uuid === currentObject.uuid);
            if (currentConnectedItem) {
                connectionPosition = (0, PanelInSlotsUtils_1.getConnectionPosition)(currentObject, connectedObject);
                if (connectionPosition &&
                    utils_1.Utils.roundTo3(this.tempVector.subVectors(connectionPosition, currentObjectStartPosition).dot(startDirection)) >= 0) {
                    cornerComponent = connectedObject;
                    break;
                }
            }
        }
        if (!cornerComponent || !connectionPosition) {
            return null;
        }
        const cornerConnectedExtrusions = cornerComponent.connectedItems
            .filter((item) => item.uuid !== currentObject.uuid)
            .map((item) => this.viewer.getObjectById(item.uuid))
            .filter((object) => object && object instanceof __1.ExtrusionObjectsBase);
        if (!cornerConnectedExtrusions.length) {
            return null;
        }
        const cornerBox = (_a = cornerComponent.mesh.geometry.boundingBox) === null || _a === void 0 ? void 0 : _a.clone().applyMatrix4(cornerComponent.matrixWorld);
        if (!cornerBox) {
            return null;
        }
        const cornerPlanes = utils_1.Utils.getBox3Planes(cornerBox);
        const cornerFrontPlane = cornerPlanes.find((plane) => utils_1.Utils.areVectorsCollinear(plane.normal, startDirection) && !utils_1.Utils.areVectorsEqual(plane.normal, startDirection));
        if (!cornerFrontPlane) {
            return null;
        }
        const points = [];
        const startPoint = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotBottomPlane)(currentSlot), (0, PanelInSlotsUtils_1.getNotchPlane)(cornerFrontPlane));
        points.push(startPoint);
        const inPanelCornerPlanes = cornerPlanes.filter((plane) => utils_1.Utils.areVectorsPerpendicular(this.panelPlane.normal, plane.normal));
        let currentPlane = cornerFrontPlane;
        const deltaAngle = utils_1.Utils.areVectorsEqual(currentSlot.normal, cornerFrontPlane.normal.clone().applyAxisAngle(this.panelPlane.normal, Math.PI / 2))
            ? Math.PI / 2
            : -Math.PI / 2;
        let nextExtrusion;
        let nextExtrusionFound = false;
        const currentDirection = startDirection.clone();
        for (let i = 0; i < 3; i += 1) {
            if (nextExtrusionFound) {
                break;
            }
            const nextPlaneNormal = currentPlane.normal.clone().applyAxisAngle(this.panelPlane.normal, deltaAngle);
            const nextPlane = inPanelCornerPlanes.find((plane) => utils_1.Utils.areVectorsEqual(plane.normal, nextPlaneNormal));
            if (!nextPlane) {
                break;
            }
            const point = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getNotchPlane)(currentPlane), (0, PanelInSlotsUtils_1.getNotchPlane)(nextPlane));
            points.push(point);
            currentPlane = nextPlane;
            nextExtrusion = cornerConnectedExtrusions.find((extrusion) => utils_1.Utils.areVectorsCollinear(extrusion.directionNormal, nextPlane.normal));
            nextExtrusionFound = !!nextExtrusion;
            if (!nextExtrusionFound) {
                currentDirection.applyAxisAngle(this.panelPlane.normal, deltaAngle);
            }
        }
        if (!nextExtrusion) {
            return null;
        }
        const nextSlot = (_b = nextExtrusion
            .getSlots()) === null || _b === void 0 ? void 0 : _b.find((slot) => utils_1.Utils.areVectorsEqual(slot.normal, currentDirection.clone().negate()) &&
            utils_1.Utils.areEqual(this.panelPlane.distanceToPoint(slot.surfacePoint), 0));
        if (!nextSlot) {
            return null;
        }
        const endPoint = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getNotchPlane)(currentPlane), (0, PanelInSlotsUtils_1.getSlotBottomPlane)(nextSlot));
        points.push(endPoint);
        const startPosition = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, currentPlane, (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(nextSlot, false));
        this.addNotch(cornerComponent.uuid, cornerBox, PanelInSlotsConsts_1.NotchType.Corner);
        return {
            object: nextExtrusion,
            contourDirection: currentPlane.normal,
            points,
            startPosition,
            slot: nextSlot,
        };
    }
    handleOppositeSlotFasteners(contourDirection, { connectedObjects, currentObject, currentSlot, currentObjectStartPosition }, previousObject, nextObjectData, hasInSlotExtrusions) {
        const oppositeFastenersData = this.getOppositeSlotFasteners(contourDirection, connectedObjects, currentObject, currentSlot, previousObject ? [previousObject.uuid, nextObjectData.object.uuid] : [nextObjectData.object.uuid]);
        if (oppositeFastenersData.length) {
            let minPoint = nextObjectData.startPosition;
            if (hasInSlotExtrusions) {
                nextObjectData.points.forEach((point) => {
                    if (!minPoint || utils_1.Utils.getNearestPoint(currentObjectStartPosition, [point, minPoint]) === point) {
                        minPoint = point;
                    }
                });
            }
            let resultPoints = [];
            oppositeFastenersData.forEach((fastenerData) => {
                if (fastenerData.fastenerPoints.some((point) => {
                    return (utils_1.Utils.getNearestPoint(currentObjectStartPosition, [point, minPoint]) === point &&
                        this.tempVector.subVectors(point, currentObjectStartPosition).dot(contourDirection) > 0);
                })) {
                    resultPoints = resultPoints.concat(fastenerData.fastenerPoints);
                    this.addNotch(fastenerData.fastenerId, fastenerData.fastenerBox, PanelInSlotsConsts_1.NotchType.Fastener);
                }
            });
            if (resultPoints.length) {
                // eslint-disable-next-line no-param-reassign
                nextObjectData.points = resultPoints.concat(nextObjectData.points);
            }
        }
    }
    getOppositeSlotFasteners(startDirection, connectedObjects, currentObject, currentSlot, exludeIds) {
        const results = [];
        const oppositeSlot = (0, PanelInSlotsUtils_1.getOppositeSlot)(this.panelPlane, currentObject, currentSlot);
        if (!oppositeSlot) {
            return [];
        }
        connectedObjects.forEach((object) => {
            var _a;
            if (exludeIds.includes(object.uuid)) {
                return;
            }
            const fastenerResult = { fastenerId: null, fastenerBox: null, fastenerPoints: [] };
            const slotNumbers = (_a = object.connectedItems.find((item) => item.uuid === currentObject.uuid)) === null || _a === void 0 ? void 0 : _a.slotNumbers;
            if (!(slotNumbers === null || slotNumbers === void 0 ? void 0 : slotNumbers.includes(oppositeSlot.slotNumber))) {
                return;
            }
            const oppositeStartPosition = utils_1.Utils.getObjectEndPoint(currentObject, startDirection.clone().negate());
            const fastenerBoxAndId = (0, PanelInSlotsUtils_1.getConnectionFastenerData)(oppositeStartPosition, this.panelPlane, currentObject, object, types_1.FASTENER_TYPES.SOCKET_HEAD_CAP_SCREWS);
            const fastenerBox = fastenerBoxAndId === null || fastenerBoxAndId === void 0 ? void 0 : fastenerBoxAndId.box;
            if (!fastenerBox || !(0, PanelInSlotsUtils_1.isSlotIntersectFastener)(currentSlot, fastenerBox)) {
                return;
            }
            fastenerResult.fastenerBox = fastenerBox;
            fastenerResult.fastenerId = fastenerBoxAndId.id;
            const fastenerBoxPlanes = utils_1.Utils.getBox3Planes(fastenerBox);
            let fastenerFrontPlane = fastenerBoxPlanes.find((plane) => !utils_1.Utils.areVectorsEqual(startDirection, plane.normal) &&
                utils_1.Utils.areVectorsCollinear(startDirection, plane.normal));
            if (!fastenerFrontPlane) {
                return;
            }
            fastenerFrontPlane = (0, PanelInSlotsUtils_1.getNotchPlane)(fastenerFrontPlane);
            const currentSlotBottomPlane = (0, PanelInSlotsUtils_1.getSlotBottomPlane)(currentSlot);
            const fastenerPoint1 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, currentSlotBottomPlane, fastenerFrontPlane);
            fastenerResult.fastenerPoints.push(fastenerPoint1);
            let fastenerParallelPlane = fastenerBoxPlanes.find((plane) => utils_1.Utils.areVectorsEqual(currentSlot.normal, plane.normal));
            if (!fastenerParallelPlane) {
                return;
            }
            fastenerParallelPlane = (0, PanelInSlotsUtils_1.getNotchPlane)(fastenerParallelPlane);
            const fastenerPoint2 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, fastenerParallelPlane, fastenerFrontPlane);
            fastenerResult.fastenerPoints.push(fastenerPoint2);
            let fastenerBackPlane = fastenerBoxPlanes.find((plane) => utils_1.Utils.areVectorsEqual(startDirection, plane.normal));
            if (!fastenerBackPlane) {
                return;
            }
            fastenerBackPlane = (0, PanelInSlotsUtils_1.getNotchPlane)(fastenerBackPlane);
            const fastenerPoint3 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, fastenerParallelPlane, fastenerBackPlane);
            fastenerResult.fastenerPoints.push(fastenerPoint3);
            const fastenerPoint4 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, fastenerBackPlane, currentSlotBottomPlane);
            fastenerResult.fastenerPoints.push(fastenerPoint4);
            results.push(fastenerResult);
        });
        return results;
    }
    // eslint-disable-next-line class-methods-use-this
    handleEndOfCurrentExtrusion(currentDirection, currentObject, currentSlot) {
        const points = [];
        const currentObjectEndPoint = utils_1.Utils.getObjectEndPoint(currentObject, currentDirection);
        const endExtrusionPlane = (0, PanelInSlotsUtils_1.getNotchPlane)(new three_1.Plane().setFromNormalAndCoplanarPoint(currentDirection, currentObjectEndPoint));
        points.push((0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotBottomPlane)(currentSlot), endExtrusionPlane));
        const oppositeSlot = (0, PanelInSlotsUtils_1.getOppositeSlot)(this.panelPlane, currentObject, currentSlot);
        if (!oppositeSlot) {
            return null;
        }
        points.push((0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotBottomPlane)(oppositeSlot), endExtrusionPlane));
        const startPosition = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(oppositeSlot, false), endExtrusionPlane);
        this.addNotch(currentObject.uuid, utils_1.Utils.getAABBox(currentObject), PanelInSlotsConsts_1.NotchType.Extrusion);
        return {
            object: currentObject,
            contourDirection: currentDirection.clone().negate(),
            points,
            startPosition,
            slot: oppositeSlot,
        };
    }
    handleEndVerticalExtrusion(startDirection, { connectedObjects, currentObject, currentObjectStartPosition, currentSlot }) {
        var _a, _b;
        let nextObject;
        let nextObjectSlot;
        let currentDirection = startDirection.clone();
        let isNotchAdded = false;
        const verticalExtrusion = this.getNextVerticalExtrusion(utils_1.Utils.areVectorsNotEqual(currentObject.directionNormal, currentDirection)
            ? currentObject.position
            : currentObject.position
                .clone()
                .add(currentObject.directionNormal.clone().multiplyScalar(currentObject.getLength())), connectedObjects);
        if (!verticalExtrusion) {
            return null;
        }
        currentDirection = currentSlot.normal.clone();
        const slots = verticalExtrusion.getSlots();
        if (!slots) {
            return null;
        }
        const connectedSlotsNumbers = (_a = verticalExtrusion.connectedItems.find((item) => item.uuid === currentObject.uuid)) === null || _a === void 0 ? void 0 : _a.slotNumbers;
        const connectedSlots = slots.filter((slot) => connectedSlotsNumbers === null || connectedSlotsNumbers === void 0 ? void 0 : connectedSlotsNumbers.includes(slot.slotNumber));
        if (!connectedSlots || !connectedSlotsNumbers) {
            return null;
        }
        const points = [];
        const startFastenerData = this.getVerticalExtrusionStartFastenerData(currentObject, currentSlot, startDirection, currentObjectStartPosition, verticalExtrusion, connectedSlots[0]);
        const startFastenerPoints = startFastenerData.fastenerPoints;
        if (startFastenerPoints.length) {
            startFastenerPoints.forEach((point) => points.push(point));
            this.addNotch(startFastenerData.fastenerId, startFastenerData.fastenerBox, PanelInSlotsConsts_1.NotchType.Fastener);
        }
        else {
            const intersectionWithVerticalExtrusion = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotBottomPlane)(currentSlot), (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(connectedSlots[0], true));
            points.push(intersectionWithVerticalExtrusion);
        }
        let currentNotConnectedSlot = (0, PanelInSlotsUtils_1.getNearestSlot)(currentSlot.surfacePoint, slots.filter((slot) => !(connectedSlotsNumbers === null || connectedSlotsNumbers === void 0 ? void 0 : connectedSlotsNumbers.includes(slot.slotNumber))));
        if ((0, PanelInSlotsUtils_1.arePerpendicularSlots)(connectedSlots[0], currentNotConnectedSlot)) {
            const intersection = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(connectedSlots[0], true), (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(currentNotConnectedSlot, true));
            points.push(intersection);
            const axisVector = new three_1.Vector3().crossVectors(connectedSlots[0].normal, currentNotConnectedSlot.normal);
            currentDirection.applyAxisAngle(axisVector, Math.PI / 2).normalize();
            this.addNotch(currentObject.uuid, utils_1.Utils.getAABBox(verticalExtrusion), PanelInSlotsConsts_1.NotchType.Extrusion);
            isNotchAdded = true;
        }
        let i = 0;
        let currentNotConnectedSlotNumber = currentNotConnectedSlot.slotNumber;
        const slotsCount = slots.length;
        const delta = (0, PanelInSlotsUtils_1.getSlotsLoopDirection)(slotsCount, connectedSlotsNumbers, currentNotConnectedSlotNumber);
        while (i < slotsCount) {
            if (connectedSlotsNumbers === null || connectedSlotsNumbers === void 0 ? void 0 : connectedSlotsNumbers.includes(currentNotConnectedSlotNumber)) {
                break;
            }
            const nextConnectedItem = verticalExtrusion.connectedItems.find(
            // eslint-disable-next-line no-loop-func
            (connectedItem) => {
                var _a;
                if (!((_a = connectedItem.slotNumbers) === null || _a === void 0 ? void 0 : _a.includes(currentNotConnectedSlot.slotNumber))) {
                    return false;
                }
                const object = this.viewer.getObjectById(connectedItem.uuid);
                if (!object) {
                    return false;
                }
                return this.inPanelPlaneObjects.includes(object);
            });
            if (nextConnectedItem && nextConnectedItem.itemType === 'Extrusion') {
                nextObject = this.viewer.getObjectById(nextConnectedItem.uuid);
                if (!nextObject || nextObject === currentObject) {
                    return null;
                }
                nextObjectSlot = (_b = nextObject
                    .getSlots()) === null || _b === void 0 ? void 0 : _b.find((slot) => utils_1.Utils.areVectorsEqual(slot.normal, currentDirection.clone().negate()) &&
                    utils_1.Utils.areEqual(this.panelPlane.distanceToPoint(slot.surfacePoint), 0, consts_1.default.THREE_FLOAT_EPSILON));
                if (!nextObjectSlot) {
                    return null;
                }
                const endFastenerData = this.getVerticalExtrusionEndFastenerData(currentObjectStartPosition, currentSlot, verticalExtrusion, currentNotConnectedSlot, nextObject, nextObjectSlot);
                const endFastenerPoints = endFastenerData.fastenerPoints;
                if (endFastenerPoints.length) {
                    endFastenerPoints.forEach((point) => points.push(point));
                    this.addNotch(endFastenerData.fastenerId, endFastenerData.fastenerBox, PanelInSlotsConsts_1.NotchType.Fastener);
                }
                else {
                    const intersectionPoint = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(currentNotConnectedSlot, true), (0, PanelInSlotsUtils_1.getSlotBottomPlane)(nextObjectSlot));
                    points.push(intersectionPoint);
                }
                const nextStartPosition = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(currentNotConnectedSlot, false), (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(nextObjectSlot, false));
                return nextObject
                    ? {
                        object: nextObject,
                        contourDirection: (0, PanelInSlotsUtils_1.getContourDirectionForVerticalExtrusions)(startDirection, currentSlot.normal, nextObjectSlot.normal),
                        points,
                        startPosition: nextStartPosition,
                        slot: nextObjectSlot,
                    }
                    : null;
            }
            if (delta > 0) {
                currentNotConnectedSlotNumber =
                    currentNotConnectedSlotNumber === slots.length ? 1 : currentNotConnectedSlotNumber + delta;
            }
            else {
                currentNotConnectedSlotNumber =
                    currentNotConnectedSlotNumber === 1 ? slots.length : currentNotConnectedSlotNumber + delta;
            }
            const nextSlot = slots[currentNotConnectedSlotNumber - 1];
            if ((0, PanelInSlotsUtils_1.arePerpendicularSlots)(currentNotConnectedSlot, nextSlot)) {
                const intersection = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(currentNotConnectedSlot, true), (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(nextSlot, true));
                points.push(intersection);
                const axisVector = new three_1.Vector3().crossVectors(currentNotConnectedSlot.normal, nextSlot.normal);
                currentDirection.applyAxisAngle(axisVector, Math.PI / 2).normalize();
                if (nextObject && !isNotchAdded) {
                    this.addNotch(currentObject.uuid, utils_1.Utils.getAABBox(verticalExtrusion), PanelInSlotsConsts_1.NotchType.Extrusion);
                    isNotchAdded = true;
                }
            }
            currentNotConnectedSlot = nextSlot;
            i += delta;
        }
        return null;
    }
    getVerticalExtrusionEndFastenerData(startPosition, currentObjectSlot, verticalExtrusion, verticalExtrusionCurrentSlot, nextObject, nextObjectSlot) {
        const result = { fastenerId: null, fastenerBox: null, fastenerPoints: [] };
        const fastenerConnectionData = (0, PanelInSlotsUtils_1.getConnectionFastenerData)(startPosition, this.panelPlane, nextObject, verticalExtrusion);
        if (!fastenerConnectionData) {
            return result;
        }
        const fastenerBox = fastenerConnectionData.box;
        result.fastenerBox = fastenerBox;
        result.fastenerId = fastenerConnectionData.id;
        const fastenerParallelPlanePoint = utils_1.Utils.getNearestPoint(currentObjectSlot.surfacePoint, [
            fastenerBox.min,
            fastenerBox.max,
        ]);
        let fastenerParallelPlane = new three_1.Plane().setFromNormalAndCoplanarPoint(nextObjectSlot.normal, fastenerParallelPlanePoint);
        const nextSlotBottomPlane = (0, PanelInSlotsUtils_1.getSlotBottomPlane)(nextObjectSlot);
        const nextSlotBottomPoint = nextSlotBottomPlane.coplanarPoint(new three_1.Vector3());
        if (this.tempVector.subVectors(fastenerParallelPlanePoint, nextSlotBottomPoint).dot(nextObjectSlot.normal) < 0) {
            return result;
        }
        fastenerParallelPlane = (0, PanelInSlotsUtils_1.getNotchPlane)(fastenerParallelPlane);
        const fastenerPoint1 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(verticalExtrusionCurrentSlot, true), fastenerParallelPlane);
        result.fastenerPoints.push(fastenerPoint1);
        const fastenerBackPlanePoint = utils_1.Utils.getNearestPoint(nextObjectSlot.surfacePoint, [
            fastenerBox.min,
            fastenerBox.max,
        ]);
        const fastenerBackPlane = (0, PanelInSlotsUtils_1.getNotchPlane)(new three_1.Plane().setFromNormalAndCoplanarPoint(nextObjectSlot.direction, fastenerBackPlanePoint));
        const fastenerPoint2 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, fastenerParallelPlane, fastenerBackPlane);
        result.fastenerPoints.push(fastenerPoint2);
        const fastenerPoint3 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, fastenerBackPlane, nextSlotBottomPlane);
        result.fastenerPoints.push(fastenerPoint3);
        return result;
    }
    getVerticalExtrusionStartFastenerData(currentObject, currentSlot, startDirection, startPosition, verticalExtrusion, verticalExtrusionConnectedSlot) {
        const result = { fastenerId: null, fastenerBox: null, fastenerPoints: [] };
        const fastenerBoxAndId = (0, PanelInSlotsUtils_1.getConnectionFastenerData)(startPosition, this.panelPlane, currentObject, verticalExtrusion);
        const fastenerBox = fastenerBoxAndId === null || fastenerBoxAndId === void 0 ? void 0 : fastenerBoxAndId.box;
        if (!fastenerBox) {
            return result;
        }
        result.fastenerBox = fastenerBox;
        result.fastenerId = fastenerBoxAndId.id;
        const fastenerFrontPlanePoint = utils_1.Utils.getNearestPoint(startPosition, [fastenerBox.min, fastenerBox.max]);
        const fastenerFrontPlane = (0, PanelInSlotsUtils_1.getNotchPlane)(new three_1.Plane().setFromNormalAndCoplanarPoint(startDirection.clone().negate(), fastenerFrontPlanePoint));
        const slotBottomPlane = (0, PanelInSlotsUtils_1.getSlotBottomPlane)(currentSlot);
        let fastenerParallelPlane = utils_1.Utils.getBox3PlaneByNormal(fastenerBox, currentSlot.normal);
        if (!fastenerParallelPlane) {
            return result;
        }
        const fastenerParallelPlanePoint = fastenerParallelPlane.coplanarPoint(new three_1.Vector3());
        const slotBottomPoint = slotBottomPlane.coplanarPoint(new three_1.Vector3());
        if (this.tempVector.subVectors(fastenerParallelPlanePoint, slotBottomPoint).dot(currentSlot.normal) < 0) {
            return result;
        }
        fastenerParallelPlane = (0, PanelInSlotsUtils_1.getNotchPlane)(fastenerParallelPlane);
        const fastenerPoint1 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, slotBottomPlane, fastenerFrontPlane);
        result.fastenerPoints.push(fastenerPoint1);
        const fastenerPoint2 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, fastenerParallelPlane, fastenerFrontPlane);
        result.fastenerPoints.push(fastenerPoint2);
        const fastenerPoint3 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, fastenerParallelPlane, (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(verticalExtrusionConnectedSlot, true));
        result.fastenerPoints.push(fastenerPoint3);
        return result;
    }
    addNotch(objectId, objectBox, notchType) {
        if (this.panel.isWireMesh()) {
            return;
        }
        if (this.notches.some((notch) => {
            var _a, _b;
            return notch.notchType === notchType &&
                ((_a = notch.objectBox) === null || _a === void 0 ? void 0 : _a.min) === (objectBox === null || objectBox === void 0 ? void 0 : objectBox.min) &&
                ((_b = notch.objectBox) === null || _b === void 0 ? void 0 : _b.max) === (objectBox === null || objectBox === void 0 ? void 0 : objectBox.max);
        })) {
            return;
        }
        this.notches.push({
            objectId,
            notchType,
            objectBox,
        });
    }
    handleInSlotExtrusions(currentDirection, { connectedObjects, currentObject, currentSlot, currentObjectStartPosition }) {
        const inSlotExtrusions = [];
        const currentSlotPlane = new three_1.Plane().setFromNormalAndCoplanarPoint(currentSlot.normal, currentSlot.surfacePoint);
        for (let i = 0; i < connectedObjects.length; i += 1) {
            const connectedObject = connectedObjects[i];
            if (!(connectedObject instanceof __1.ExtrusionObjectsBase)) {
                // eslint-disable-next-line no-continue
                continue;
            }
            const connectionPosition = (0, PanelInSlotsUtils_1.getConnectionPosition)(currentObject, connectedObject);
            if (connectionPosition && utils_1.Utils.roundTo2(currentSlotPlane.distanceToPoint(connectionPosition)) === 0) {
                inSlotExtrusions.push(connectedObject);
            }
        }
        if (!inSlotExtrusions || !inSlotExtrusions.length) {
            return null;
        }
        const closestExtrusionData = (0, PanelInSlotsUtils_1.getClosestExtrusion)(this.panelPlane, currentObjectStartPosition, currentDirection, currentSlot, inSlotExtrusions);
        if (!closestExtrusionData ||
            !closestExtrusionData.extrusion ||
            !closestExtrusionData.startPosition ||
            !closestExtrusionData.slot) {
            return null;
        }
        const nextObject = closestExtrusionData.extrusion;
        const nextObjectStartPosition = closestExtrusionData.startPosition;
        const nextObjectSlot = closestExtrusionData.slot;
        const contourDirection = (0, PanelInSlotsUtils_1.getInSlotItemsContourDirection)(currentDirection, nextObject, currentSlot);
        const points = [];
        const { fastenerId, fastenerPoints, fastenerBox } = this.getInSlotExtusionFastenerPoints(currentObject, currentSlot, currentObjectStartPosition, currentDirection, nextObject, nextObjectSlot);
        if (fastenerPoints.length) {
            fastenerPoints.forEach((point) => points.push(point));
            this.addNotch(fastenerId, fastenerBox, PanelInSlotsConsts_1.NotchType.Fastener);
        }
        else {
            const intersectionPoint = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotBottomPlane)(currentSlot), (0, PanelInSlotsUtils_1.getSlotBottomPlane)(nextObjectSlot));
            points.push(intersectionPoint);
        }
        return {
            object: nextObject,
            contourDirection,
            points,
            startPosition: nextObjectStartPosition,
            slot: nextObjectSlot,
        };
    }
    getInSlotExtusionFastenerPoints(currentObject, currentSlot, startPosition, currentDirection, nextObject, nextObjectSlot) {
        const result = { fastenerId: null, fastenerBox: null, fastenerPoints: [] };
        const fastenerConnectionData = (0, PanelInSlotsUtils_1.getConnectionFastenerData)(startPosition, this.panelPlane, currentObject, nextObject);
        if (!fastenerConnectionData) {
            return result;
        }
        const fastenerBox = fastenerConnectionData.box;
        if (!(0, PanelInSlotsUtils_1.isSlotIntersectFastener)(nextObjectSlot, fastenerBox)) {
            return result;
        }
        result.fastenerBox = fastenerBox;
        result.fastenerId = fastenerConnectionData.id;
        const fastenerBoxPlanes = utils_1.Utils.getBox3Planes(fastenerBox);
        let fastenerFrontPlane = fastenerBoxPlanes.find((plane) => !utils_1.Utils.areVectorsEqual(currentDirection, plane.normal) &&
            utils_1.Utils.areVectorsCollinear(currentDirection, plane.normal));
        if (!fastenerFrontPlane) {
            return result;
        }
        fastenerFrontPlane = (0, PanelInSlotsUtils_1.getNotchPlane)(fastenerFrontPlane);
        // if fastener is further than next extrusion - do not account this fastener
        const fastenerOnSlotPoint = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(currentSlot, false), fastenerFrontPlane);
        const nextExtrusionStartPosition = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(currentSlot, false), (0, PanelInSlotsUtils_1.getSlotBottomPlane)(nextObjectSlot));
        if (utils_1.Utils.getNearestPoint(startPosition, [fastenerOnSlotPoint, nextExtrusionStartPosition]) ===
            nextExtrusionStartPosition) {
            return result;
        }
        const fastenerPoint1 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotBottomPlane)(currentSlot), fastenerFrontPlane);
        result.fastenerPoints.push(fastenerPoint1);
        let fastenerParallelPlane = fastenerBoxPlanes.find((plane) => utils_1.Utils.areVectorsEqual(currentSlot.normal, plane.normal));
        if (!fastenerParallelPlane) {
            return result;
        }
        fastenerParallelPlane = (0, PanelInSlotsUtils_1.getNotchPlane)(fastenerParallelPlane);
        let fastenerPoint2 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, fastenerParallelPlane, fastenerFrontPlane);
        const fastenerPoint3 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, fastenerParallelPlane, (0, PanelInSlotsUtils_1.getSlotBottomPlane)(nextObjectSlot));
        if (utils_1.Utils.getNearestPoint(startPosition, [fastenerPoint2, fastenerPoint3]) === fastenerPoint3) {
            fastenerPoint2 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, fastenerFrontPlane, (0, PanelInSlotsUtils_1.getSlotBottomPlane)(nextObjectSlot));
            result.fastenerPoints.push(fastenerPoint2);
        }
        else {
            if (nextObject.scalingAngle !== 0) {
                if (currentSlot.normal.dot(nextObjectSlot.normal) > 0) {
                    const nextObjectSlotPlane = (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(nextObjectSlot, false);
                    const fastenerAngledPoint1 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, nextObjectSlotPlane, fastenerFrontPlane);
                    result.fastenerPoints.push(fastenerAngledPoint1);
                    const fastenerAngledPoint2 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, nextObjectSlotPlane, fastenerParallelPlane);
                    result.fastenerPoints.push(fastenerAngledPoint2);
                }
                else {
                    result.fastenerPoints.push(fastenerPoint2);
                }
            }
            else {
                result.fastenerPoints.push(fastenerPoint2);
            }
            result.fastenerPoints.push(fastenerPoint3);
        }
        return result;
    }
    // precondition: objects` directionNormals are collinear
    handleEndToEndConnectedExtrusion(contourDirection, currentObject, nextObject, currentSlot, currentObjectStartPosition, connectionPosition) {
        var _a;
        const points = [];
        const nextSlot = (_a = nextObject.getSlots()) === null || _a === void 0 ? void 0 : _a.find((slot) => {
            return utils_1.Utils.areVectorsEqual(currentSlot.normal, slot.normal) && (0, PanelInSlotsUtils_1.isSlotInPlane)(this.panelPlane, slot);
        });
        if (!nextSlot) {
            return null;
        }
        const startPosition = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, new three_1.Plane().setFromNormalAndCoplanarPoint(currentSlot.normal, currentSlot.surfacePoint), new three_1.Plane().setFromNormalAndCoplanarPoint(contourDirection, connectionPosition));
        const { fastenerPoints, fastenerBox } = this.getEndToEndConnectionFastenerPoints(currentObjectStartPosition, currentObject, currentSlot, contourDirection, nextObject, nextSlot);
        if (fastenerPoints.length) {
            fastenerPoints.forEach((point) => points.push(point));
            this.addNotch(currentObject.uuid, fastenerBox, PanelInSlotsConsts_1.NotchType.Fastener);
        }
        else {
            const point = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotBottomPlane)(currentSlot), new three_1.Plane().setFromNormalAndCoplanarPoint(contourDirection, connectionPosition));
            points.push(point);
        }
        return {
            object: nextObject,
            contourDirection,
            points,
            startPosition,
            slot: nextSlot,
        };
    }
    // precondition: objects` directionNormals are not collinear
    handleEndToSideConnectedExtrusion(contourDirection, currentObject, nextObject, currentSlot, connectionPosition) {
        const points = [];
        let nextSlot = null;
        const tempPlane = new three_1.Plane();
        const nextExtrusionSlots = nextObject.getSlots();
        if (!nextExtrusionSlots) {
            return null;
        }
        for (let i = 0; i < nextExtrusionSlots.length; i += 1) {
            const slot = nextExtrusionSlots[i];
            tempPlane.setFromNormalAndCoplanarPoint(slot.normal, slot.surfacePoint);
            if (utils_1.Utils.roundTo2(tempPlane.distanceToPoint(connectionPosition)) === 0 &&
                utils_1.Utils.roundTo2(this.panelPlane.distanceToPoint(slot.surfacePoint)) === 0) {
                nextSlot = slot;
                break;
            }
        }
        if (!nextSlot) {
            return null;
        }
        const startPosition = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(currentSlot, false), (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(nextSlot, false));
        const fastenerPointsData = this.getEndToSideConnectionFastenerData(startPosition, currentObject, currentSlot, contourDirection, nextObject, nextSlot);
        const { fastenerId, fastenerPoints, fastenerBox } = fastenerPointsData;
        if (fastenerPoints.length) {
            fastenerPoints.forEach((point) => points.push(point));
            this.addNotch(fastenerId, fastenerBox, PanelInSlotsConsts_1.NotchType.Fastener);
        }
        else {
            const point = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotBottomPlane)(currentSlot), (0, PanelInSlotsUtils_1.getSlotBottomPlane)(nextSlot));
            points.push(point);
        }
        return {
            object: nextObject,
            contourDirection: currentSlot.normal.clone().projectOnVector(nextSlot.direction).normalize(),
            points,
            startPosition,
            slot: nextSlot,
        };
    }
    handleEndConnectedExtrusion(contourDirection, { connectedObjects, currentObject, currentSlot, currentObjectStartPosition }) {
        let nextObject = null;
        let connectionPosition = null;
        for (let i = 0; i < connectedObjects.length; i += 1) {
            const connectedObject = connectedObjects[i];
            if (!(connectedObject instanceof __1.ExtrusionObjectsBase)) {
                // eslint-disable-next-line no-continue
                continue;
            }
            const currentConnectedItem = connectedObjects[i].connectedItems.find((connectedItem) => connectedItem.uuid === currentObject.uuid);
            if (currentConnectedItem && currentConnectedItem.connectedSide === types_1.CONNECTED_FACE.END) {
                connectionPosition = (0, PanelInSlotsUtils_1.getConnectionPosition)(currentObject, connectedObject);
                if (connectionPosition &&
                    utils_1.Utils.roundTo3(this.tempVector.subVectors(connectionPosition, currentObjectStartPosition).dot(contourDirection)) >= 0 &&
                    !(0, PanelInSlotsUtils_1.isVerticalToPanelExtrusion)(this.panelPlane, connectedObject)) {
                    nextObject = connectedObject;
                    break;
                }
            }
        }
        if (!nextObject || !connectionPosition) {
            return null;
        }
        if (utils_1.Utils.areVectorsCollinear(currentObject.directionNormal, nextObject.directionNormal)) {
            return this.handleEndToEndConnectedExtrusion(contourDirection, currentObject, nextObject, currentSlot, currentObjectStartPosition, connectionPosition);
        }
        return this.handleEndToSideConnectedExtrusion(contourDirection, currentObject, nextObject, currentSlot, connectionPosition);
    }
    getEndToSideConnectionFastenerData(startPosition, currentObject, currentSlot, currentDirection, nextObject, nextObjectSlot) {
        const result = { fastenerId: null, fastenerBox: null, fastenerPoints: [] };
        const fastenerBoxAndId = (0, PanelInSlotsUtils_1.getConnectionFastenerData)(startPosition, this.panelPlane, currentObject, nextObject);
        if (!fastenerBoxAndId) {
            return result;
        }
        const fastenerBox = fastenerBoxAndId.box;
        result.fastenerBox = fastenerBox;
        result.fastenerId = fastenerBoxAndId.id;
        if (!(0, PanelInSlotsUtils_1.isSlotIntersectFastener)(currentSlot, fastenerBox)) {
            return result;
        }
        const fastenerBoxPlanes = utils_1.Utils.getBox3Planes(fastenerBox);
        const fastenerParallelPlane = fastenerBoxPlanes.find((plane) => utils_1.Utils.areVectorsEqual(plane.normal, nextObjectSlot.normal));
        if (!fastenerParallelPlane) {
            return result;
        }
        const nextObjectDirection = currentSlot.normal.clone().projectOnVector(nextObjectSlot.direction).normalize();
        const fastenerFrontPlane = fastenerBoxPlanes.find((plane) => !utils_1.Utils.areVectorsEqual(plane.normal, nextObjectDirection) &&
            utils_1.Utils.areVectorsCollinear(plane.normal, nextObjectDirection));
        if (!fastenerFrontPlane) {
            return result;
        }
        const fastenerFrontPlaneNormal = fastenerFrontPlane === null || fastenerFrontPlane === void 0 ? void 0 : fastenerFrontPlane.normal;
        const fastenerBackPlane = fastenerBoxPlanes.find((plane) => !utils_1.Utils.areVectorsEqual(fastenerFrontPlaneNormal, plane.normal) &&
            utils_1.Utils.areVectorsCollinear(fastenerFrontPlaneNormal, plane.normal));
        if (!fastenerBackPlane) {
            return result;
        }
        const currentSlotLine = new three_1.Line3(utils_1.Utils.getSlotStartPosition(currentSlot, currentDirection.clone().negate()), utils_1.Utils.getSlotStartPosition(currentSlot, currentDirection));
        let firstPlane;
        let secondPlane;
        const fastenerBoxExpanded = fastenerBox.clone().expandByScalar(PanelInSlotsConsts_1.fastenerBoxExpand);
        const currentSlotWithFrontPlaneIntersection = fastenerFrontPlane.intersectLine(currentSlotLine, this.tempVector);
        if (currentSlotWithFrontPlaneIntersection &&
            fastenerBoxExpanded.containsPoint(currentSlotWithFrontPlaneIntersection)) {
            firstPlane = fastenerParallelPlane;
            secondPlane = fastenerBackPlane;
        }
        else {
            const currentSlotWithParallelPlaneIntersection = fastenerParallelPlane.intersectLine(currentSlotLine, this.tempVector);
            if (currentSlotWithParallelPlaneIntersection &&
                fastenerBoxExpanded.containsPoint(currentSlotWithParallelPlaneIntersection)) {
                firstPlane = fastenerParallelPlane;
                secondPlane = fastenerBackPlane;
            }
            else {
                const currentSlotWithBackPlaneIntersection = fastenerBackPlane.intersectLine(currentSlotLine, this.tempVector);
                if (currentSlotWithBackPlaneIntersection &&
                    fastenerBoxExpanded.containsPoint(currentSlotWithBackPlaneIntersection)) {
                    firstPlane = fastenerBackPlane;
                }
            }
        }
        if (!firstPlane) {
            return result;
        }
        firstPlane = (0, PanelInSlotsUtils_1.getNotchPlane)(firstPlane);
        const fastenerStartPoint = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotBottomPlane)(currentSlot), firstPlane);
        result.fastenerPoints.push(fastenerStartPoint);
        if (secondPlane) {
            secondPlane = (0, PanelInSlotsUtils_1.getNotchPlane)(secondPlane);
            if (currentObject.scalingAngle !== 0) {
                const nextDirection = currentSlot.normal.clone().projectOnVector(nextObjectSlot.direction).normalize();
                const currentSlotSurfacePlane = (0, PanelInSlotsUtils_1.getSlotSurfacePlane)(currentSlot, false);
                if (currentDirection.dot(nextDirection) > 0) {
                    const fastenerAngledPoint1 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, currentSlotSurfacePlane, firstPlane);
                    result.fastenerPoints.push(fastenerAngledPoint1);
                    const fastenerAngledPoint2 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, currentSlotSurfacePlane, secondPlane);
                    result.fastenerPoints.push(fastenerAngledPoint2);
                }
                else {
                    const fastenerAngledPoint1 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotBottomPlane)(currentSlot), secondPlane);
                    result.fastenerPoints.push(fastenerAngledPoint1);
                }
            }
            else {
                const fastenerPoint = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, firstPlane, secondPlane);
                result.fastenerPoints.push(fastenerPoint);
            }
            const fastenerEndPoint = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, secondPlane, (0, PanelInSlotsUtils_1.getSlotBottomPlane)(nextObjectSlot));
            result.fastenerPoints.push(fastenerEndPoint);
        }
        else {
            const fastenerPoint2 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, firstPlane, (0, PanelInSlotsUtils_1.getSlotBottomPlane)(nextObjectSlot));
            result.fastenerPoints.push(fastenerPoint2);
        }
        return result;
    }
    getEndToEndConnectionFastenerPoints(startPosition, currentObject, currentSlot, currentDirection, nextObject, nextSlot) {
        const result = { fastenerId: null, fastenerBox: null, fastenerPoints: [] };
        const fastenerBoxAndId = (0, PanelInSlotsUtils_1.getConnectionFastenerData)(startPosition, this.panelPlane, currentObject, nextObject);
        if (!fastenerBoxAndId) {
            return result;
        }
        const fastenerBox = fastenerBoxAndId.box;
        result.fastenerBox = fastenerBox;
        result.fastenerId = fastenerBoxAndId.id;
        const fastenerBoxPlanes = utils_1.Utils.getBox3Planes(fastenerBox);
        if (!(0, PanelInSlotsUtils_1.isSlotIntersectFastener)(currentSlot, fastenerBox)) {
            return result;
        }
        let fastenerFrontPlane = fastenerBoxPlanes.find((plane) => !utils_1.Utils.areVectorsEqual(currentDirection, plane.normal) &&
            utils_1.Utils.areVectorsCollinear(currentDirection, plane.normal));
        if (!fastenerFrontPlane) {
            return result;
        }
        fastenerFrontPlane = (0, PanelInSlotsUtils_1.getNotchPlane)(fastenerFrontPlane);
        const fastenerPoint1 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, (0, PanelInSlotsUtils_1.getSlotBottomPlane)(currentSlot), fastenerFrontPlane);
        result.fastenerPoints.push(fastenerPoint1);
        let fastenerParallelPlane = fastenerBoxPlanes.find((plane) => utils_1.Utils.areVectorsEqual(currentSlot.normal, plane.normal));
        if (!fastenerParallelPlane) {
            return result;
        }
        fastenerParallelPlane = (0, PanelInSlotsUtils_1.getNotchPlane)(fastenerParallelPlane);
        const fastenerPoint2 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, fastenerParallelPlane, fastenerFrontPlane);
        result.fastenerPoints.push(fastenerPoint2);
        let fastenerBackPlane = fastenerBoxPlanes.find((plane) => utils_1.Utils.areVectorsEqual(currentDirection, plane.normal));
        if (!fastenerBackPlane) {
            return result;
        }
        fastenerBackPlane = (0, PanelInSlotsUtils_1.getNotchPlane)(fastenerBackPlane);
        const fastenerPoint3 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, fastenerParallelPlane, fastenerBackPlane);
        result.fastenerPoints.push(fastenerPoint3);
        const fastenerPoint4 = (0, PanelInSlotsUtils_1.getPlanesIntersection)(this.panelPlane, fastenerBackPlane, (0, PanelInSlotsUtils_1.getSlotBottomPlane)(nextSlot));
        result.fastenerPoints.push(fastenerPoint4);
        return result;
    }
    isNotPresentAnyOppositeExtrusion(point, slotNormal) {
        this.raycaster.set(point, slotNormal);
        this.raycaster.far = Infinity;
        // if no any inplane extrusions in perpendicular direction - it is not closed contour
        const oppositeExtrusions = this.getRayIntersections();
        return oppositeExtrusions.length === 0;
    }
    getNextVerticalExtrusion(startPoint, connectedObjects) {
        const verticalExtrusions = connectedObjects.filter((object) => (0, PanelInSlotsUtils_1.isVerticalToPanelExtrusion)(this.panelPlane, object));
        let closestExtrusion = null;
        verticalExtrusions.forEach((extrusion) => {
            const slots = extrusion.getSlots();
            if (!slots || closestExtrusion) {
                return;
            }
            const tempPlane = new three_1.Plane();
            for (let i = 0; i < slots.length; i += 1) {
                const slot = slots[i];
                if (utils_1.Utils.roundTo2(tempPlane.setFromNormalAndCoplanarPoint(slot.normal, slot.surfacePoint).distanceToPoint(startPoint)) === 0) {
                    closestExtrusion = extrusion;
                    break;
                }
            }
        });
        return closestExtrusion;
    }
    getRayIntersections(extrusions = []) {
        const intersections = this.raycaster.intersectObjects(this.inPanelPlaneObjects).filter((intersection) => {
            var _a;
            return !((_a = intersection.object.parent) === null || _a === void 0 ? void 0 : _a.name.startsWith('OBB|'));
        });
        if (!intersections.length) {
            return [];
        }
        const results = [];
        intersections.forEach((intersection) => {
            const parentObject = intersection.object.parent;
            if (parentObject instanceof __1.BaseObject3D) {
                if (extrusions.length && !extrusions.includes(parentObject)) {
                    return;
                }
                if (!results.includes(parentObject)) {
                    results.push(parentObject);
                }
            }
        });
        return results;
    }
    isObjectToHandle(object) {
        return (0, PanelInSlotsUtils_1.isInPlaneToPanelObject)(this.panelPlane, object) || (0, PanelInSlotsUtils_1.isVerticalToPanelExtrusion)(this.panelPlane, object);
    }
    validatePanelSize(panel) {
        const panelSize = panel.getSize();
        const [widthIndex, lengthIndex] = panelSize.x > panelSize.y ? [1, 0] : [0, 1];
        const panelWidth = panelSize.getComponent(widthIndex);
        const panelLength = panelSize.getComponent(lengthIndex);
        if (panelWidth > panel.partDetails.maxStockWidth || panelLength > panel.partDetails.maxStockLength) {
            this.insertionErrorMessage = 'Panel exceeds the stock size';
            this.viewer.getDesignProMain().highlightingTool.highlightObject(panel);
        }
    }
}
exports.default = PanelInSlotsHelper;
